<template>
  <div class="creator-landing">

    <!-- Hero -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-badge">Warhall Creator</div>
        <h1 class="hero-title">Create Any Wargame<br /><span class="hero-accent">In Warhall</span></h1>
        <p class="hero-sub">
          Define your own tabletop game, define every unit and faction,
          and bring them to life by adding your own models— all in one place. <span class="hero-free">And yes...it's free & public!</span>
        </p>
        <div class="hero-actions">
          <router-link class="cta-btn cta-btn--primary" :to="{ name: 'AddNewGame' }">Get Started</router-link>
          <router-link class="cta-btn cta-btn--ghost" :to="{ name: 'catalogue' }">Browse Catalogue</router-link>
        </div>
      </div>
      <div class="hero-visual">
        <div class="hero-orb hero-orb--1"></div>
        <div class="hero-orb hero-orb--2"></div>
        <div class="hero-orb hero-orb--3"></div>
        <div class="hero-icon-grid">
          <div class="hi-card">🎮</div>
          <div class="hi-card">⚔️</div>
          <div class="hi-card">🗿</div>
          <div class="hi-card">🎨</div>
        </div>
      </div>
    </section>

    <!-- Feature steps -->
    <section class="steps">
      <h2 class="section-title">Three steps to your game</h2>
      <div class="steps-grid">

        <div class="step-card">
          <div class="step-icon">🎮</div>
          <h3 class="step-title">Define Your Game</h3>
          <p class="step-body">
            Register your tabletop game on Warhall. Define its name, dice, buttons, interactions and make it available to everyone for free when finished.
          </p>
          <router-link class="step-link" :to="{ name: 'AddNewGame' }">Add a game →</router-link>
        </div>

        <div class="step-card">
          <div class="step-icon">📝</div>
          <h3 class="step-title">Edit Factions &amp; Datasheets</h3>
          <p class="step-body">
            Use Warhall's built-in data editor to define every faction, unit, stats & rules.
            Changes are saved directly
            to your game's data files and reflected immediately in the app.
          </p>
          <router-link class="step-link" :to="{ name: 'WDS3DataEditor' }">Open editor →</router-link>
        </div>

        <div class="step-card">
          <div class="step-icon">🗿</div>
          <h3 class="step-title">Upload &amp; Link 3D Models</h3>
          <p class="step-body">
            Scan a miniature, use a text prompt, or upload your own files.
            Assign the resulting skin to any unit, with support for
            mounts and equipment variants. You can also pick a model from the community catalogue and assign it to your units with a single click.
          </p>
          <router-link class="step-link" :to="{ name: 'ModelCreator' }">Open Model Creator →</router-link>
        </div>

      </div>
    </section>

    <!-- Feature highlight cards -->
    <section class="highlights">
      <h2 class="section-title">Everything you need</h2>
      <div class="highlights-grid">

        <div class="hl-card">
          <span class="hl-icon">🏗️</span>
          <h4>Game &amp; Faction Builder</h4>
          <p>Structured hierarchy editor for games, factions, units and modifiers. Works with the WDS2 data format used natively in Warhall.</p>
        </div>

        <div class="hl-card">
          <span class="hl-icon">🔬</span>
          <h4>Unit Datasheet Editor</h4>
          <p>Edit stat lines, options, equipment and rules for every unit. All changes are versioned and immediately available in-game.</p>
        </div>

        <div class="hl-card">
          <span class="hl-icon">📷</span>
          <h4>Miniature Scanner</h4>
          <p>Use the miniatures on your shelf to capture it from multiple angles and let Warhall generate a full 3D model automatically using photogrammetry AI.</p>
        </div>

        <div class="hl-card">
          <span class="hl-icon">✍️</span>
          <h4>Text-to-3D Prompt</h4>
          <p>You don't have the miniature? Describe it and generate a 3D model from a text prompt. Preview, approve and link it to any unit in seconds.</p>
        </div>

        <div class="hl-card">
          <span class="hl-icon">📦</span>
          <h4>GLB Upload</h4>
          <p>Already have a 3D model? Upload your 3D file and link it directly to a unit without any generation step.</p>
        </div>

        <div class="hl-card">
          <span class="hl-icon">🎨</span>
          <h4>Skin Catalogue</h4>
          <p>Find thousands of available skins in the community catalogue. Browse and reuse skins to your units with a single click.</p>
        </div>

      </div>
    </section>

    <!-- Community section -->
    <section class="community">
      <div class="community-inner">
        <div class="community-text">
          <div class="hero-badge">Community</div>
          <h2 class="community-title">Don't want to start from scratch?</h2>
          <p class="community-body">
            Games are already live on Warhall, built and maintained by their communities.
            Join an existing game — help expand its unit roster, upload models for missing factions,
            or contribute skins that other players will enjoy at the table.
          </p>
          <p class="community-body">
            Every contribution makes the whole community richer.
          </p>
          <div class="hero-actions" style="margin-top: 28px;">
            <router-link class="cta-btn cta-btn--primary" :to="{ name: 'ModifyGame' }">Browse &amp; Edit Games</router-link>
            <router-link class="cta-btn cta-btn--ghost" :to="{ name: 'catalogue' }">Explore Skin Catalogue</router-link>
          </div>
        </div>
        <div class="community-icons">
          <div class="ci-item"><span>🤝</span><small>Collaborate</small></div>
          <div class="ci-item"><span>🌍</span><small>Community</small></div>
          <div class="ci-item"><span>📖</span><small>Open Games</small></div>
          <div class="ci-item"><span>🏆</span><small>Contribute</small></div>
        </div>
      </div>
    </section>

    <!-- CTA footer band -->
    <section class="cta-band">
      <div class="cta-band-inner">
        <h2>Ready to build?</h2>
        <p>Sign in and start adding your game to Warhall today.</p>
        <div class="hero-actions">
          <router-link class="cta-btn cta-btn--primary" :to="{ name: 'AddNewGame' }">Add Your Game</router-link>
          <router-link class="cta-btn cta-btn--ghost" :to="{ name: 'ModelCreator' }">Create a Model</router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'CreatorLanding',
};
</script>

<style scoped>
/* ── Base ──────────────────────────────────────────────────────────────────── */
.creator-landing {
  min-height: 100vh;
  background: #0a121e;
  color: #e8eef5;
  font-family: 'Arial', sans-serif;
}

.section-title {
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  color: #e8eef5;
  margin: 0 0 48px;
}

/* ── Hero ──────────────────────────────────────────────────────────────────── */
.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 100px 32px 80px;
}

.hero-content {
  flex: 1;
  max-width: 560px;
}

.hero-badge {
  display: inline-block;
  background: rgba(36, 113, 163, 0.25);
  border: 1px solid rgba(36, 113, 163, 0.5);
  color: #5dade2;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
}

.hero-title {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 20px;
  color: #f0f6ff;
}

.hero-accent {
  background: linear-gradient(90deg, #2471a3, #5dade2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-tagline {
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 20px;
  letter-spacing: -0.01em;
}

.hero-sub {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #8fafc8;
  margin: 0 0 36px;
}

.hero-free {
  color: #ffffff;
  font-size: 1.25em;
  font-weight: 700;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.cta-btn {
  display: inline-block;
  padding: 13px 28px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  transition: background 0.18s, border-color 0.18s, transform 0.12s;
}

.cta-btn--primary {
  background: #2471a3;
  color: #fff;
  border: 2px solid #2471a3;
}

.cta-btn--primary:hover {
  background: #1a5276;
  border-color: #1a5276;
  transform: translateY(-1px);
}

.cta-btn--ghost {
  background: transparent;
  color: #5dade2;
  border: 2px solid #2471a3;
}

.cta-btn--ghost:hover {
  background: rgba(36, 113, 163, 0.15);
  transform: translateY(-1px);
}

/* Hero visual */
.hero-visual {
  position: relative;
  width: 340px;
  height: 300px;
  flex-shrink: 0;
}

.hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.35;
}

.hero-orb--1 {
  width: 220px;
  height: 220px;
  background: #2471a3;
  top: 0;
  left: 20px;
}

.hero-orb--2 {
  width: 160px;
  height: 160px;
  background: #1abc9c;
  bottom: 20px;
  right: 0;
}

.hero-orb--3 {
  width: 100px;
  height: 100px;
  background: #9b59b6;
  top: 60px;
  right: 40px;
}

.hero-icon-grid {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: center;
  justify-items: center;
  padding: 20px;
}

.hi-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  width: 90px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  backdrop-filter: blur(8px);
  transition: transform 0.2s;
}

.hi-card:hover {
  transform: scale(1.06);
}

/* ── Steps ─────────────────────────────────────────────────────────────────── */
.steps {
  padding: 80px 32px;
  max-width: 1100px;
  margin: 0 auto;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.step-card {
  background: #111d2e;
  border: 1px solid #1e3048;
  border-radius: 16px;
  padding: 36px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: border-color 0.2s, transform 0.2s;
}

.step-card:hover {
  border-color: #2471a3;
  transform: translateY(-3px);
}

.step-num {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: #2471a3;
}

.step-icon {
  font-size: 2.2rem;
}

.step-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #e8eef5;
  margin: 0;
}

.step-body {
  font-size: 0.9rem;
  line-height: 1.7;
  color: #7a9ab8;
  margin: 0;
  flex: 1;
}

.step-link {
  color: #5dade2;
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: none;
  margin-top: 4px;
  transition: color 0.15s;
}

.step-link:hover {
  color: #aed6f1;
}

/* ── Highlights ────────────────────────────────────────────────────────────── */
.highlights {
  padding: 80px 32px;
  background: #0d1520;
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 1100px;
  margin: 0 auto;
}

.hl-card {
  background: #111d2e;
  border: 1px solid #1e3048;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.2s;
}

.hl-card:hover {
  border-color: #2471a3;
}

.hl-icon {
  font-size: 1.8rem;
}

.hl-card h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #cfe2f3;
  margin: 0;
}

.hl-card p {
  font-size: 0.87rem;
  line-height: 1.65;
  color: #6e90ad;
  margin: 0;
}

/* ── Community ──────────────────────────────────────────────────────────────── */
.community {
  padding: 80px 32px;
  background: #0a121e;
  border-top: 1px solid #1e3048;
}

.community-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 60px;
}

.community-text {
  flex: 1;
}

.community-title {
  font-size: 1.9rem;
  font-weight: 800;
  color: #f0f6ff;
  margin: 16px 0 20px;
  line-height: 1.2;
}

.community-body {
  font-size: 0.97rem;
  line-height: 1.75;
  color: #7a9ab8;
  margin: 0 0 12px;
}

.community-icons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  flex-shrink: 0;
}

.ci-item {
  background: #111d2e;
  border: 1px solid #1e3048;
  border-radius: 14px;
  width: 110px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: border-color 0.2s;
}

.ci-item:hover {
  border-color: #2471a3;
}

.ci-item span {
  font-size: 2rem;
}

.ci-item small {
  font-size: 0.75rem;
  color: #7a9ab8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── CTA band ──────────────────────────────────────────────────────────────── */
.cta-band {
  padding: 80px 32px;
  background: linear-gradient(135deg, #0e1e33 0%, #132640 100%);
  border-top: 1px solid #1e3048;
  text-align: center;
}

.cta-band-inner {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.cta-band h2 {
  font-size: 2rem;
  font-weight: 800;
  margin: 0;
  color: #f0f6ff;
}

.cta-band p {
  font-size: 1rem;
  color: #7a9ab8;
  margin: 0 0 8px;
}

/* ── Responsive ─────────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .hero {
    flex-direction: column;
    padding: 60px 24px 48px;
    text-align: center;
  }

  .hero-content {
    max-width: 100%;
  }

  .hero-actions {
    justify-content: center;
  }

  .hero-visual {
    width: 260px;
    height: 220px;
  }

  .steps-grid,
  .highlights-grid {
    grid-template-columns: 1fr;
  }

  .community-inner {
    flex-direction: column;
    text-align: center;
  }

  .community-icons {
    justify-items: center;
  }

  .hero-title {
    font-size: 2.2rem;
  }
}

@media (max-width: 600px) {
  .steps-grid,
  .highlights-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.4rem;
  }
}
</style>
